/* eslint-disable react/jsx-key */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';

import { TableScrollButtons } from './TableScrollButtons';
import { useIsScrollable } from '../../../../../../../common/react/hooks/use-is-scrollable';

const SCROLL_DEBOUNCE_MS = 10;

/**
 * @param root0
 * @param root0.children
 * @param root0.getTableProps
 * @param root0.nextPageButton
 * @param root0.modifier
 * @param root0.cssClass
 * @param root0.stickyHeader
 * @param root0.stickyPageNav
 * @param root0.stickyMainNav
 */
export function StatsTable({
    children,
    getTableProps,
    nextPageButton,
    modifier,
    cssClass,
    stickyHeader,
    stickyPageNav
}) {
    const scrollWrapperRef = useRef();
    const wrapperRef = useRef();
    const stickyBarWrapperRef = useRef();

    const [isHidden, setIsHidden] = useState(true);
    const [scrollPosition, setScrollPosition] = useState(0);
    const isTableScrollable = useIsScrollable(scrollWrapperRef, wrapperRef);

    // Debounce needs to be set as a function to work correctly and then be called
    const debounceScrollPosition = _.debounce((value) => {
        setScrollPosition(value);
    }, SCROLL_DEBOUNCE_MS);

    const getHeaderOffset = (element) => {
        const rect = element?.getBoundingClientRect();
        return rect?.height;
    };

    const getOffset = (element) => {
        const pageHeader = getHeaderOffset(stickyPageNav);

        const rect = element?.getBoundingClientRect(),
            scrollTop =
                window.pageYOffset || document.documentElement.scrollTop;

        return rect?.top + scrollTop - pageHeader;
    };

    const listenToScroll = () => {
        let heightToHideFrom = getOffset(scrollWrapperRef.current);
        const winScroll =
            document.body.scrollTop || document.documentElement.scrollTop;
        const tableElement = getHeaderOffset(scrollWrapperRef.current);
        const tableEnd = heightToHideFrom + tableElement;

        if (winScroll > tableEnd || winScroll < heightToHideFrom) {
            setIsHidden(true);
        } else {
            setIsHidden(false);
        }
    };

    const handleScroll = (e) => {
        debounceScrollPosition(e.target.scrollLeft);
    };

    // ===== Effects ======================================================== //

    useEffect(() => {
        window.addEventListener('scroll', listenToScroll);
        return () => window.removeEventListener('scroll', listenToScroll);
    }, []);

    return (
        <ScrollSync>
            <>
                {isHidden ? (
                    ''
                ) : (
                    <div
                        className={classNames(
                            'stats-table ' + (cssClass ?? ''),
                            {
                                'stats-table--is-scrollable': isTableScrollable,
                                [`stats-table--${modifier}`]: !!modifier
                            },
                            'stats-table__dup'
                        )}
                    >
                        <ScrollSyncPane>
                            <div
                                className="stats-table__wrapper stats-table__dup-header stats-table__scroll-pane js-scrollable-container"
                                ref={stickyBarWrapperRef}
                            >
                                {isTableScrollable && (
                                    <TableScrollButtons
                                        scrollWrapperRef={scrollWrapperRef}
                                        wrapperRef={wrapperRef}
                                        scrollPosition={scrollPosition}
                                    />
                                )}
                                <table className="stats-table__table">
                                    {stickyHeader}
                                </table>
                            </div>
                        </ScrollSyncPane>
                    </div>
                )}
                <div
                    className={classNames('stats-table ' + (cssClass ?? ''), {
                        'stats-table--is-scrollable': isTableScrollable,
                        [`stats-table--${modifier}`]: !!modifier
                    })}
                    ref={wrapperRef}
                >
                    <ScrollSyncPane>
                        <div
                            style={{ overflow: 'auto', overflowY: 'hidden' }}
                            className="stats-table__wrapper js-scrollable-container"
                            onScroll={handleScroll}
                        >
                            {isTableScrollable && (
                                <TableScrollButtons
                                    scrollWrapperRef={scrollWrapperRef}
                                    wrapperRef={wrapperRef}
                                    scrollPosition={scrollPosition}
                                />
                            )}

                            <table
                                ref={scrollWrapperRef}
                                {...getTableProps()}
                                className="stats-table__table"
                            >
                                {children}
                            </table>
                        </div>
                    </ScrollSyncPane>

                    {nextPageButton}
                </div>
            </>
        </ScrollSync>
    );
}

StatsTable.propTypes = {
    children: PropTypes.any.isRequired,
    getTableProps: PropTypes.func.isRequired,
    nextPageButton: PropTypes.any,
    modifier: PropTypes.string,
    cssClass: PropTypes.string,
    stickyHeader: PropTypes.element,
    stickyPageNav: PropTypes.any,
    stickyMainNav: PropTypes.any
};
